import * as React from "react";
import { useContext, useMemo, useState } from "react";
import styled from "styled-components";
import sortBy from "lodash/sortBy";
import upperCase from "lodash/upperCase";
import groupBy from "lodash/groupBy";
import { useTranslation } from "react-i18next";
import { Button } from "components/bootstrap";
import generateId from "logic/generateId";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import usePluginEntities from "hooks/usePluginEntities";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import WidgetFocusContext from "views/components/contexts/WidgetFocusContext";
import SectionInfo from "../SectionInfo";
import SectionSubheadline from "../SectionSubheadline";
const Group = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;
const CreateButton = styled(Button)`
  display: block;
  margin: 5px 0;
  width: 100%;
`;
export const isCreatorFunc = (creator) => "func" in creator;
const CreateMenuItem = ({
  creator,
  onClick,
  setOverflowingComponents
}) => {
  var _a;
  const { t } = useTranslation("search");
  const sendTelemetry = useSendTelemetry("search-sidebar");
  const dispatch = useViewsDispatch();
  const disabled = ((_a = creator.useCondition) == null ? void 0 : _a.call(creator)) === false;
  const { setWidgetEditing } = useContext(WidgetFocusContext);
  const createHandlerFor = () => {
    const telemetry = () => {
      var _a2;
      return sendTelemetry(
        (_a2 = TELEMETRY_EVENT_TYPE.SEARCH_WIDGET_CREATE[upperCase(creator.title).replace(/ /g, "_")]) != null ? _a2 : `Search Widget ${creator.title} Created`,
        {
          event_details: {
            widgetType: creator.type,
            widgetTitle: creator.title
          }
        }
      );
    };
    if (isCreatorFunc(creator)) {
      return () => {
        telemetry();
        onClick();
        dispatch(creator.func()).then((widgetId) => {
          setWidgetEditing(widgetId);
        });
      };
    }
    if (creator.component) {
      const CreatorComponent = creator.component;
      return () => {
        telemetry();
        const id = generateId();
        const onClose = () => {
          setOverflowingComponents((cur) => {
            const newState = { ...cur };
            delete newState[id];
            return newState;
          });
          onClick();
        };
        const renderedComponent = /* @__PURE__ */ React.createElement(CreatorComponent, { key: creator.title, onClose });
        setOverflowingComponents((cur) => {
          const newState = { ...cur };
          newState[id] = renderedComponent;
          return newState;
        });
      };
    }
    throw new Error(`Invalid binding for creator: ${JSON.stringify(creator)} - has neither 'func' nor 'component'.`);
  };
  return /* @__PURE__ */ React.createElement(CreateButton, { key: creator.title, onClick: createHandlerFor(), disabled }, creator.titleKey ? t(creator.titleKey) : creator.title);
};
const GroupCreateMenuItems = ({
  creators,
  onClick,
  setOverflowingComponents
}) => /* @__PURE__ */ React.createElement(React.Fragment, null, sortBy(creators, "title").map((creator) => /* @__PURE__ */ React.createElement(
  CreateMenuItem,
  {
    creator,
    key: creator.title,
    onClick,
    setOverflowingComponents
  }
)));
const AddWidgetButton = ({ onClick }) => {
  const { t } = useTranslation("search");
  const [overflowingComponents, setOverflowingComponents] = useState({});
  const creators = usePluginEntities("creators");
  const {
    preset: presets,
    generic,
    events: eventsCreator,
    investigations: investigationsCreator
  } = useMemo(() => groupBy(creators, (creator) => creator.type), [creators]);
  const components = Object.values(overflowingComponents);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(SectionInfo, null, t("sidebar_create.info")), /* @__PURE__ */ React.createElement(Group, null, /* @__PURE__ */ React.createElement(SectionSubheadline, null, t("sidebar_create.generic")), /* @__PURE__ */ React.createElement(
    GroupCreateMenuItems,
    {
      creators: generic,
      onClick,
      setOverflowingComponents
    }
  )), /* @__PURE__ */ React.createElement(Group, null, /* @__PURE__ */ React.createElement(SectionSubheadline, null, t("sidebar_create.predefined_aggregation")), /* @__PURE__ */ React.createElement(
    GroupCreateMenuItems,
    {
      creators: presets,
      onClick,
      setOverflowingComponents
    }
  )), !!(investigationsCreator == null ? void 0 : investigationsCreator.length) && /* @__PURE__ */ React.createElement(Group, null, /* @__PURE__ */ React.createElement(SectionSubheadline, null, t("sidebar_create.investigations")), /* @__PURE__ */ React.createElement(
    GroupCreateMenuItems,
    {
      creators: investigationsCreator,
      onClick,
      setOverflowingComponents
    }
  )), !!(eventsCreator == null ? void 0 : eventsCreator.length) && /* @__PURE__ */ React.createElement(Group, null, /* @__PURE__ */ React.createElement(SectionSubheadline, null, t("sidebar_create.events")), /* @__PURE__ */ React.createElement(
    GroupCreateMenuItems,
    {
      creators: eventsCreator,
      onClick,
      setOverflowingComponents
    }
  )), components);
};
export default AddWidgetButton;
